import { SearchSvg } from '../assets/icons';
import AIFeedbackSvg from '../assets/icons/AIFeedbackSvg';
import AllCustomizableLessonsSvg from '../assets/icons/AllCustomizableLessonsSvg';
import BilingualSvg from '../assets/icons/BilingualSvg';
import BrainbreaksSvg from '../assets/icons/BrainbreaksSvg';
import ClassroomBuzzSvg from '../assets/icons/ClassRoomBuzzSvg';
import CompetitionSvg from '../assets/icons/CompetitionSvg';
import CriticalThinkingSvg from '../assets/icons/CriticalThinkingSvg';
import CurifySvg from '../assets/icons/CurifySvg';
import DailyRoutinesSvg from '../assets/icons/DailyRoutinesSvg';
import FormativeAssessmentSvg from '../assets/icons/FormativeAssessmentSvg';
import ForTheSubSvg from '../assets/icons/ForTheSubSvg';
import ForYouSvg from '../assets/icons/ForYouSvg';
import FullLessonIconSvg from '../assets/icons/FullLessonIconSvg';
import QuickSvg from '../assets/icons/QuickSvg';
import QuickWritesSvg from '../assets/icons/QuickWritesSvg';
import ReadingComprehensionSvg from '../assets/icons/ReadingComprehensionSvg';
import RocketSvg from '../assets/icons/RocketSvg';
import StaarPrepSvg from '../assets/icons/StaarPrepSvg';
import TestPrepSvg from '../assets/icons/TestPrepSvg';
import ToolkitSvg from '../assets/icons/ToolkitSvg';
import TurnAndTalkSvg from '../assets/icons/TurnAndTalkSvg';
import VocabSvg from '../assets/icons/VocabSvg';
import WowSvg from '../assets/icons/WowSvg';
import WriteMoreSvg from '../assets/icons/WriteMoreSvg';

type SubCategoryType =
  | 'generators'
  | 'lessons'
  | 'results'
  | 'recent_generators'
  | 'latest_generators'
  | 'custom_generators';

type ContentSubCategory = {
  id: string;
  title: string;
  type: SubCategoryType;
};

export type ContentCategory = {
  id: string;
  title: string;
  icon: React.ReactElement;
  subCategories: ContentSubCategory[];
};

export type ContentHierarchy = DiscoveryContentMapper[];

type DiscoveryContentMapper = {
  subject: string;
  subjectDisplayName: string;
  categories: ContentCategory[];
};

export const subjects: string[] = [
  'ELA',
  'Social studies',
  'Science',
  'Math',
  'World languages',
  'Mental health',
  'Other',
];

export const subjectOptions: {
  value: string;
  displayName: string;
}[] = [
  { value: 'ela', displayName: 'English Language Arts' },
  { value: 'social_studies', displayName: 'Social Studies' },
  { value: 'science', displayName: 'Science' },
  { value: 'math', displayName: 'Math' },
  { value: 'world_languages', displayName: 'World Languages' },
  { value: 'mental_health', displayName: 'Mental Health' },
  { value: 'other', displayName: 'Other' },
];

export const contentHierarchy: ContentHierarchy = [
  {
    subject: 'none',
    subjectDisplayName: '',
    categories: [
      {
        id: 'for_you',
        title: 'For You',
        icon: <ForYouSvg />,
        subCategories: [
          {
            id: 'want_to_try',
            type: 'custom_generators',
            title: 'You might want to try...',
          },
          {
            id: 'latest',
            type: 'latest_generators',
            title: 'New in Curipod',
          },
          {
            id: 'recent_generators',
            type: 'recent_generators',
            title: 'Recent customizable lessons',
          },
          {
            id: 'recent_results',
            type: 'results',
            title: 'Recent reports',
          },
          {
            id: 'recent_lessons',
            type: 'lessons',
            title: 'Recent lessons',
          },
        ],
      },
      {
        id: 'write_more',
        title: 'Write more',
        icon: <WriteMoreSvg />,
        subCategories: [
          {
            id: 'creative_writing',
            title: 'Creative writing',
            type: 'generators',
          },
          {
            id: 'writing_routines',
            title: 'Daily writing practice',
            type: 'generators',
          },
          {
            id: 'students_get_ai_feedback',
            title: 'Students receive AI feedback',
            type: 'generators',
          },
        ],
      },
      {
        id: 'reading_comprehension',
        title: 'Reading comprehension',
        icon: <ReadingComprehensionSvg />,
        subCategories: [
          {
            id: 'show_comprehension_by_drawing',
            title: 'Show comprehension by drawing',
            type: 'generators',
          },
          {
            id: 'i_have_my_text',
            title: 'Activities based on text resource',
            type: 'generators',
          },
          {
            id: 'generate_my_text',
            title: 'Generate new text resource',
            type: 'generators',
          },
        ],
      },
      {
        id: 'vocabulary_and_grammar',
        title: 'Vocabulary & grammar',
        icon: <VocabSvg />,
        subCategories: [
          {
            id: 'content_specific_vocabulary',
            title: 'Vocabulary practice',
            type: 'generators',
          },
          {
            id: 'students_get_ai_feedback',
            title: 'Students receive AI feedback',
            type: 'generators',
          },
          {
            id: 'figurative_language',
            title: 'Exploring figurative language',
            type: 'generators',
          },
        ],
      },
      {
        id: 'ai_feedback',
        title: 'Students receive AI Feedback',
        icon: <AIFeedbackSvg />,
        subCategories: [
          {
            id: 'daily_writing_practice',
            title: 'Daily writing practice',
            type: 'generators',
          },
          {
            id: 'students_use_feedback_to_improve',
            title: 'Students use feedback to improve',
            type: 'generators',
          },
          {
            id: 'frequent_feedback_opportunities',
            title: 'Frequent feedback opportunities',
            type: 'generators',
          },
        ],
      },
      {
        id: 'staar_prep',
        title: 'STAAR Prep',
        icon: <StaarPrepSvg />,
        subCategories: [
          {
            id: 'staar_ela',
            title: 'RLA',
            type: 'generators',
          },
          {
            id: 'staar_rla_spanish',
            title: 'RLA Spanish',
            type: 'generators',
          },
          {
            id: 'staar_social_studies',
            title: 'Social Studies & U.S. History',
            type: 'generators',
          },
          {
            id: 'staar_science',
            title: 'Science (Coming soon!)',
            type: 'generators',
          },
          {
            id: 'staar_math',
            title: 'Math ',
            type: 'generators',
          },
        ],
      },
      {
        id: 'test_prep_made_fun',
        title: 'Test prep made fun!',
        icon: <RocketSvg />,
        subCategories: [
          {
            id: 'college_entrance_exams',
            title: 'College Entrance Exams',
            type: 'generators',
          },
          {
            id: 'sbac',
            title: 'SBAC',
            type: 'generators',
          },
          {
            id: 'staar',
            title: 'TEXAS - STAAR & TELPAS',
            type: 'generators',
          },
          {
            id: 'mstep',
            title: 'M-STEP',
            type: 'generators',
          },
          {
            id: 'wida',
            title: 'WIDA',
            type: 'generators',
          },
          {
            id: 'california_sbac_caaspp',
            title: 'CALIFORNIA – SBAC & CAASPP',
            type: 'generators',
          },
          {
            id: 'ny_stp',
            title: 'NEW YORK – NYS Exam Prep',
            type: 'generators',
          },
          {
            id: 'north_carolina_test_prep',
            title: 'NORTH CAROLINA - NC Test Prep',
            type: 'generators',
          },
          {
            id: 'pennsylvania_pssa',
            title: 'Pennsylvania - PSSA',
            type: 'generators',
          },
          {
            id: 'georgia_gmas',
            title: 'Georgia - Milestones',
            type: 'generators',
          },
          {
            id: 'colorado_cmas',
            title: 'Colorado - CMAS',
            type: 'generators',
          },
          {
            id: 'arizona_aasa',
            title: 'Arizona AASA',
            type: 'generators',
          },
          {
            id: 'ohio_ost',
            title: 'Ohio - OST',
            type: 'generators',
          },
          {
            id: 'ohio_oelpa',
            title: 'Ohio - OELPA',
            type: 'generators',
          },
          {
            id: 'west_virginia_wvgsa',
            title: 'West Virginia - WVGSA',
            type: 'generators',
          },
          {
            id: 'westvirginia_elpa21',
            title: 'West Virginia - ELPA21',
            type: 'generators',
          },
        ],
      },
      {
        id: 'wow',
        title: 'WOW',
        icon: <WowSvg />,
        subCategories: [
          {
            id: 'captivating_writing_prompts',
            title: 'Captivating writing prompts',
            type: 'generators',
          },
          {
            id: 'students_get_ai_feedback',
            title: 'Students receive AI feedback',
            type: 'generators',
          },
          {
            id: 'students_decide_input',
            title: 'Students decide input',
            type: 'generators',
          },
          {
            id: 'amazing_student_drawings',
            title: 'Amazing student drawings',
            type: 'generators',
          },
        ],
      },
      {
        id: 'daily_routines',
        title: 'Daily routines',
        icon: <DailyRoutinesSvg />,
        subCategories: [
          {
            id: 'brain_breaks_and_energizers',
            title: 'Brain breaks and energizers',
            type: 'generators',
          },
          {
            id: 'morning_meeting',
            title: 'Morning meeting',
            type: 'generators',
          },
          {
            id: 'bell_ringers',
            title: 'Bell ringers',
            type: 'generators',
          },
          {
            id: 'start_and_end_the_lesson',
            title: 'Start and end the lesson',
            type: 'generators',
          },
        ],
      },
      {
        id: 'full_lessons',
        title: 'Full lessons',
        icon: <FullLessonIconSvg />,
        subCategories: [],
      },
      {
        id: 'brain_breaks',
        title: 'Brain breaks',
        icon: <BrainbreaksSvg />,
        subCategories: [
          {
            id: 'academic',
            title: 'Academic',
            type: 'generators',
          },
          {
            id: 'just_for_fun',
            title: 'Just for fun',
            type: 'generators',
          },
        ],
      },
      {
        id: 'curify',
        title: 'Curify',
        icon: <CurifySvg />,
        subCategories: [],
      },
    ],
  },
  {
    subject: 'ela',
    subjectDisplayName: 'English Language Arts',
    categories: [
      {
        id: 'none',
        title: 'Your search',
        icon: <SearchSvg />,
        subCategories: [],
      },
      {
        id: 'write_more',
        title: 'Write more',
        icon: <WriteMoreSvg />,
        subCategories: [
          {
            id: 'creative_writing',
            title: 'Creative writing',
            type: 'generators',
          },
          {
            id: 'writing_routines',
            title: 'Daily writing practice',
            type: 'generators',
          },
          {
            id: 'students_get_ai_feedback',
            title: 'Students receive AI feedback',
            type: 'generators',
          },
        ],
      },
      {
        id: 'reading_comprehension',
        title: 'Reading comprehension',
        icon: <ReadingComprehensionSvg />,
        subCategories: [
          {
            id: 'i_have_my_text',
            title: 'Activities based on text resource',
            type: 'generators',
          },
          {
            id: 'show_comprehension_by_drawing',
            title: 'Show comprehension by drawing',
            type: 'generators',
          },
          {
            id: 'generate_my_text',
            title: 'Generate new text resource',
            type: 'generators',
          },
        ],
      },
      {
        id: 'vocabulary_and_grammar',
        title: 'Vocabulary & grammar',
        icon: <VocabSvg />,
        subCategories: [
          {
            id: 'content_specific_vocabulary',
            title: 'Content-specific vocabulary',
            type: 'generators',
          },
          {
            id: 'students_get_ai_feedback',
            title: 'Students receive AI feedback',
            type: 'generators',
          },
          {
            id: 'figurative_language',
            title: 'Exploring figurative language',
            type: 'generators',
          },
        ],
      },
      {
        id: 'ai_feedback',
        title: 'Students receive AI Feedback',
        icon: <AIFeedbackSvg />,
        subCategories: [
          {
            id: 'daily_writing_practice',
            title: 'Daily writing practice',
            type: 'generators',
          },
          {
            id: 'students_use_feedback_to_improve',
            title: 'Students use feedback to improve',
            type: 'generators',
          },
          {
            id: 'frequent_feedback_opportunities',
            title: 'Frequent feedback opportunities',
            type: 'generators',
          },
        ],
      },
      {
        id: 'wow',
        title: 'WOW',
        icon: <WowSvg />,
        subCategories: [
          {
            id: 'captivating_writing_prompts',
            title: 'CAPTIVATING WRITING PROMPTS',
            type: 'generators',
          },
          {
            id: 'students_get_ai_feedback',
            title: 'Students receive AI feedback',
            type: 'generators',
          },
          {
            id: 'students_decide_input',
            title: 'Students decide input',
            type: 'generators',
          },
          {
            id: 'amazing_student_drawings',
            title: 'Amazing student drawings',
            type: 'generators',
          },
        ],
      },
      {
        id: 'daily_routines',
        title: 'Daily routines',
        icon: <DailyRoutinesSvg />,
        subCategories: [
          {
            id: 'brain_breaks_and_energizers',
            title: 'Brain breaks and energizers',
            type: 'generators',
          },
          {
            id: 'morning_meeting',
            title: 'Morning meeting',
            type: 'generators',
          },
          {
            id: 'bell_ringers',
            title: 'Bell ringers',
            type: 'generators',
          },
          {
            id: 'start_and_end_the_lesson',
            title: 'Start and end the lesson',
            type: 'generators',
          },
        ],
      },
      {
        id: 'full_lessons',
        title: 'Full lessons',
        icon: <FullLessonIconSvg />,
        subCategories: [],
      },
      {
        id: 'brain_breaks',
        title: 'Brain breaks',
        icon: <BrainbreaksSvg />,
        subCategories: [
          {
            id: 'academic',
            title: 'Academic',
            type: 'generators',
          },
          {
            id: 'just_for_fun',
            title: 'Just for fun',
            type: 'generators',
          },
        ],
      },
      {
        id: 'curify',
        title: 'Curify',
        icon: <CurifySvg />,
        subCategories: [],
      },
    ],
  },
  {
    subject: 'math',
    categories: [],
    subjectDisplayName: 'Math',
  },
  {
    subject: 'science',
    categories: [
      {
        id: 'none',
        title: 'Your search',
        icon: <SearchSvg />,
        subCategories: [],
      },

      {
        id: 'write_in_science',
        title: 'Write in science',
        icon: <WriteMoreSvg />,
        subCategories: [],
      },
      {
        id: 'formative_assessment',
        title: 'Formative assessment',
        icon: <FormativeAssessmentSvg />,
        subCategories: [],
      },
      {
        id: 'turn_and_talk',
        title: 'Turn and talk',
        icon: <TurnAndTalkSvg />,
        subCategories: [],
      },
      {
        id: 'for_the_sub',
        title: 'For the sub',
        icon: <ForTheSubSvg />,
        subCategories: [],
      },
      {
        id: 'full_lessons',
        title: 'Full lessons',
        icon: <FullLessonIconSvg />,
        subCategories: [],
      },
      {
        id: 'curify',
        title: 'Curify',
        icon: <CurifySvg />,
        subCategories: [],
      },
      {
        id: 'brain_breaks',
        title: 'Brain breaks',
        icon: <BrainbreaksSvg />,
        subCategories: [],
      },
    ],
    subjectDisplayName: 'Science',
  },
  {
    subject: 'social_studies',
    categories: [
      {
        id: 'none',
        title: 'Your search',
        icon: <SearchSvg />,
        subCategories: [],
      },
      {
        id: 'classroom_buzz',
        title: 'Classroom buzz',
        icon: <ClassroomBuzzSvg />,
        subCategories: [],
      },
      {
        id: 'full_lessons',
        title: 'Full lessons',
        icon: <FullLessonIconSvg />,
        subCategories: [],
      },
      {
        id: 'curify',
        title: 'Curify',
        icon: <CurifySvg />,
        subCategories: [],
      },
      {
        id: 'brain_breaks',
        title: 'Brain breaks',
        icon: <BrainbreaksSvg />,
        subCategories: [],
      },
      {
        id: 'critical_thinking',
        title: 'Critical thinking',
        icon: <CriticalThinkingSvg />,
        subCategories: [],
      },
      {
        id: 'formative_assessment',
        title: 'Formative assessment',
        icon: <FormativeAssessmentSvg />,
        subCategories: [],
      },
    ],
    subjectDisplayName: 'Social studies',
  },
  {
    subject: 'world_languages',
    categories: [],
    subjectDisplayName: 'World languages',
  },
  {
    subject: 'mental_health',
    categories: [],
    subjectDisplayName: 'Mental health',
  },
  {
    subject: 'other',
    categories: [],
    subjectDisplayName: 'Other',
  },
];
export const fall24ContentHierarchy: ContentHierarchy = [
  {
    subject: 'none',
    subjectDisplayName: '',
    categories: [
      {
        id: 'for_you',
        title: 'For You',
        icon: <ForYouSvg />,
        subCategories: [
          {
            id: 'want_to_try',
            type: 'custom_generators',
            title: 'You might want to try...',
          },
          {
            id: 'latest',
            type: 'latest_generators',
            title: 'New in Curipod',
          },
          {
            id: 'recent_generators',
            type: 'recent_generators',
            title: 'Recent customizable lessons',
          },
          {
            id: 'recent_lessons',
            type: 'lessons',
            title: 'Recent lessons',
          },
          {
            id: 'recent_results',
            type: 'results',
            title: 'Recent reports',
          },
        ],
      },
      {
        id: 'five_min_writing',
        title: '5 Min Writing Practice',
        icon: <QuickSvg />,
        subCategories: [],
      },
      {
        id: 'quick_writes',
        title: 'Story-Based Writing Practice',
        icon: <QuickWritesSvg />,
        subCategories: [
          {
            id: 'argumentative_writing',
            title: 'Argumentative writing',
            type: 'generators',
          },
          {
            id: 'narrative_writing',
            title: 'Narrative writing',
            type: 'generators',
          },
          {
            id: 'informational_writing',
            title: 'Informational writing',
            type: 'generators',
          },
        ],
      },
      {
        id: 'test_prep_made_fun',
        title: 'Test Prep',
        icon: <TestPrepSvg />,
        subCategories: [
          {
            id: 'college_entrance_exams',
            title: 'College Entrance Exams',
            type: 'generators',
          },
          {
            id: 'sbac',
            title: 'SBAC',
            type: 'generators',
          },
          {
            id: 'staar',
            title: 'TEXAS - STAAR & TELPAS',
            type: 'generators',
          },
          {
            id: 'mstep',
            title: 'M-STEP',
            type: 'generators',
          },
          {
            id: 'wida',
            title: 'WIDA',
            type: 'generators',
          },
          {
            id: 'california_sbac_caaspp',
            title: 'CALIFORNIA – SBAC & CAASPP',
            type: 'generators',
          },
          {
            id: 'ny_stp',
            title: 'NEW YORK – NYS Exam Prep',
            type: 'generators',
          },
          {
            id: 'north_carolina_test_prep',
            title: 'NORTH CAROLINA - NC Test Prep',
            type: 'generators',
          },
          {
            id: 'pennsylvania_pssa',
            title: 'Pennsylvania - PSSA',
            type: 'generators',
          },
          {
            id: 'georgia_gmas',
            title: 'Georgia - Milestones',
            type: 'generators',
          },
          {
            id: 'colorado_cmas',
            title: 'Colorado - CMAS',
            type: 'generators',
          },
          {
            id: 'arizona_aasa',
            title: 'Arizona AASA',
            type: 'generators',
          },
          {
            id: 'ohio_ost',
            title: 'Ohio - OST',
            type: 'generators',
          },
          {
            id: 'ohio_oelpa',
            title: 'Ohio - OELPA',
            type: 'generators',
          },
          {
            id: 'west_virginia_wvgsa',
            title: 'West Virginia - WVGSA',
            type: 'generators',
          },
          {
            id: 'westvirginia_elpa21',
            title: 'West Virginia - ELPA21',
            type: 'generators',
          },
        ],
      },
      {
        id: 'daily_routines',
        title: 'Daily Routines',
        icon: <DailyRoutinesSvg />,
        subCategories: [
          {
            id: 'vocabulary_practice',
            title: 'Vocabulary practice',
            type: 'generators',
          },
          {
            id: 'bell_ringers',
            title: 'Bell ringers',
            type: 'generators',
          },
          {
            id: 'exit_tickets',
            title: 'Exit tickets',
            type: 'generators',
          },
          {
            id: 'brain_breaks',
            title: 'Brain breaks',
            type: 'generators',
          },
          {
            id: 'social_emotional_learning',
            title: 'Social emotional learning',
            type: 'generators',
          },
          {
            id: 'draw_it',
            title: 'Draw it!',
            type: 'generators',
          },
        ],
      },
      {
        id: 'games_and_competitions',
        title: 'Games & Competitions',
        icon: <CompetitionSvg />,
        subCategories: [
          {
            id: 'curi_quiz',
            title: 'Curi-quiz',
            type: 'generators',
          },
          {
            id: 'making_inferences',
            title: 'Making Inferences',
            type: 'generators',
          },
          {
            id: 'vocabulary_games',
            title: 'Vocabulary games',
            type: 'generators',
          },
          {
            id: 'escape_rooms',
            title: 'Escape rooms',
            type: 'generators',
          },
        ],
      },
      {
        id: 'toolkit',
        title: 'Toolkit',
        icon: <ToolkitSvg />,
        subCategories: [],
      },
      {
        id: 'bilingual',
        title: 'Bilingual',
        icon: <BilingualSvg />,
        subCategories: [],
      },
      {
        id: 'all',
        title: 'All',
        icon: <AllCustomizableLessonsSvg />,
        subCategories: [],
      },
    ],
  },
  {
    subject: 'ela',
    subjectDisplayName: 'English Language Arts',
    categories: [
      {
        id: 'none',
        title: 'Your search',
        icon: <SearchSvg />,
        subCategories: [],
      },
      {
        id: 'five_min_writing',
        title: '5 Min Writing Practice',
        icon: <QuickSvg />,
        subCategories: [],
      },
      {
        id: 'quick_writes',
        title: 'Story-Based Writing Practice',
        icon: <QuickWritesSvg />,
        subCategories: [
          {
            id: 'argumentative_writing',
            title: 'Argumentative writing',
            type: 'generators',
          },
          {
            id: 'narrative_writing',
            title: 'Narrative writing',
            type: 'generators',
          },
          {
            id: 'informational_writing',
            title: 'Informational writing',
            type: 'generators',
          },
        ],
      },
      {
        id: 'test_prep_made_fun',
        title: 'Test Prep',
        icon: <TestPrepSvg />,
        subCategories: [
          {
            id: 'college_entrance_exams',
            title: 'College Entrance Exams',
            type: 'generators',
          },
          {
            id: 'sbac',
            title: 'SBAC',
            type: 'generators',
          },
          {
            id: 'staar',
            title: 'TEXAS - STAAR & TELPAS',
            type: 'generators',
          },
          {
            id: 'mstep',
            title: 'M-STEP',
            type: 'generators',
          },
          {
            id: 'wida',
            title: 'WIDA',
            type: 'generators',
          },
          {
            id: 'california_sbac_caaspp',
            title: 'CALIFORNIA – SBAC & CAASPP',
            type: 'generators',
          },
          {
            id: 'ny_stp',
            title: 'NEW YORK – NYS Exam Prep',
            type: 'generators',
          },
          {
            id: 'north_carolina_test_prep',
            title: 'NORTH CAROLINA - NC Test Prep',
            type: 'generators',
          },
          {
            id: 'pennsylvania_pssa',
            title: 'Pennsylvania - PSSA',
            type: 'generators',
          },
          {
            id: 'georgia_gmas',
            title: 'Georgia - Milestones',
            type: 'generators',
          },
          {
            id: 'colorado_cmas',
            title: 'Colorado - CMAS',
            type: 'generators',
          },
          {
            id: 'arizona_aasa',
            title: 'Arizona AASA',
            type: 'generators',
          },
          {
            id: 'ohio_ost',
            title: 'Ohio - OST',
            type: 'generators',
          },
          {
            id: 'ohio_oelpa',
            title: 'Ohio - OELPA',
            type: 'generators',
          },
          {
            id: 'west_virginia_wvgsa',
            title: 'West Virginia - WVGSA',
            type: 'generators',
          },
          {
            id: 'westvirginia_elpa21',
            title: 'West Virginia - ELPA21',
            type: 'generators',
          },
        ],
      },
      {
        id: 'daily_routines',
        title: 'Daily Routines',
        icon: <DailyRoutinesSvg />,
        subCategories: [
          {
            id: 'vocabulary_practice',
            title: 'Vocabulary practice',
            type: 'generators',
          },
          {
            id: 'bell_ringers',
            title: 'Bell ringers',
            type: 'generators',
          },
          {
            id: 'exit_tickets',
            title: 'Exit tickets',
            type: 'generators',
          },
          {
            id: 'brain_breaks',
            title: 'Brain breaks',
            type: 'generators',
          },
          {
            id: 'social_emotional_learning',
            title: 'Social emotional learning',
            type: 'generators',
          },
          {
            id: 'draw_it',
            title: 'Draw it!',
            type: 'generators',
          },
        ],
      },
      {
        id: 'games_and_competitions',
        title: 'Games & Competitions',
        icon: <CompetitionSvg />,
        subCategories: [
          {
            id: 'curi_quiz',
            title: 'Curi-quiz',
            type: 'generators',
          },
          {
            id: 'making_inferences',
            title: 'Making Inferences',
            type: 'generators',
          },
          {
            id: 'vocabulary_games',
            title: 'Vocabulary games',
            type: 'generators',
          },
          {
            id: 'escape_rooms',
            title: 'Escape rooms',
            type: 'generators',
          },
        ],
      },
      {
        id: 'toolkit',
        title: 'Toolkit',
        icon: <ToolkitSvg />,
        subCategories: [],
      },
      {
        id: 'bilingual',
        title: 'Bilingual',
        icon: <BilingualSvg />,
        subCategories: [],
      },
      {
        id: 'curify',
        title: 'Curify',
        icon: <CurifySvg />,
        subCategories: [],
      },
    ],
  },
  {
    subject: 'math',
    categories: [],
    subjectDisplayName: 'Math',
  },
  {
    subject: 'science',
    categories: [
      {
        id: 'none',
        title: 'Your search',
        icon: <SearchSvg />,
        subCategories: [],
      },

      {
        id: 'write_in_science',
        title: 'Write in science',
        icon: <WriteMoreSvg />,
        subCategories: [],
      },
      {
        id: 'formative_assessment',
        title: 'Formative assessment',
        icon: <FormativeAssessmentSvg />,
        subCategories: [],
      },
      {
        id: 'turn_and_talk',
        title: 'Turn and talk',
        icon: <TurnAndTalkSvg />,
        subCategories: [],
      },
      {
        id: 'for_the_sub',
        title: 'For the sub',
        icon: <ForTheSubSvg />,
        subCategories: [],
      },
      {
        id: 'full_lessons',
        title: 'Full lessons',
        icon: <FullLessonIconSvg />,
        subCategories: [],
      },
      {
        id: 'curify',
        title: 'Curify',
        icon: <CurifySvg />,
        subCategories: [],
      },
      {
        id: 'brain_breaks',
        title: 'Brain breaks',
        icon: <BrainbreaksSvg />,
        subCategories: [],
      },
    ],
    subjectDisplayName: 'Science',
  },
  {
    subject: 'social_studies',
    categories: [
      {
        id: 'none',
        title: 'Your search',
        icon: <SearchSvg />,
        subCategories: [],
      },
      {
        id: 'classroom_buzz',
        title: 'Classroom buzz',
        icon: <ClassroomBuzzSvg />,
        subCategories: [],
      },
      {
        id: 'full_lessons',
        title: 'Full lessons',
        icon: <FullLessonIconSvg />,
        subCategories: [],
      },
      {
        id: 'curify',
        title: 'Curify',
        icon: <CurifySvg />,
        subCategories: [],
      },
      {
        id: 'brain_breaks',
        title: 'Brain breaks',
        icon: <BrainbreaksSvg />,
        subCategories: [],
      },
      {
        id: 'critical_thinking',
        title: 'Critical thinking',
        icon: <CriticalThinkingSvg />,
        subCategories: [],
      },
      {
        id: 'formative_assessment',
        title: 'Formative assessment',
        icon: <FormativeAssessmentSvg />,
        subCategories: [],
      },
    ],
    subjectDisplayName: 'Social studies',
  },
  {
    subject: 'world_languages',
    categories: [],
    subjectDisplayName: 'World languages',
  },
  {
    subject: 'mental_health',
    categories: [],
    subjectDisplayName: 'Mental health',
  },
  {
    subject: 'other',
    categories: [],
    subjectDisplayName: 'Other',
  },
];
